// src/pages/admin/AdminAndroidApp.jsx
import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import {
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { storage, db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const formatBytes = (b) => {
  if (!b) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / 1024 ** i).toFixed(1)} ${units[i]}`;
};

const AdminAndroidApp = () => {
  const { user, isAdmin, isBoard, loading } = useAuth();

  const [file, setFile] = useState(null);
  const [versionName, setVersionName] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // { type, msg }
  const [latest, setLatest] = useState(null); // { url, version, uploadedAt, uploadedBy }

  /* ---- Live view of the current release doc ---- */
  useEffect(() => {
    if (!user || !(isAdmin || isBoard)) return undefined;
    const unsub = onSnapshot(
      doc(db, 'appReleases', 'android'),
      (snap) => {
        if (snap.exists()) setLatest(snap.data());
        else setLatest(null);
      },
      (err) => console.warn('appReleases listener:', err),
    );
    return () => unsub();
  }, [user, isAdmin, isBoard]);

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin && !isBoard) return <Navigate to="/" replace />;

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setStatus(null);
    setProgress(0);
    // Try to pre-fill version from filename like "ercsa-2.3.1.apk"
    if (f) {
      const m = f.name.match(/(\d+\.\d+\.\d+)/);
      if (m) setVersionName(m[1]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!/\.apk$/i.test(file.name)) {
      setStatus({ type: 'error', msg: 'Only .apk files are allowed.' });
      return;
    }
    if (file.size > 150 * 1024 * 1024) {
      setStatus({ type: 'error', msg: 'APK must be under 150 MB.' });
      return;
    }

    setUploading(true);
    setProgress(0);
    setStatus(null);

    try {
      const path = `android-app/releases/${Date.now()}-${file.name.replace(/[^\w.\-]/g, '_')}`;
      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, file, {
        contentType: 'application/vnd.android.package-archive',
      });

      task.on(
        'state_changed',
        (snap) =>
          setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        (err) => {
          setUploading(false);
          setStatus({ type: 'error', msg: `Upload failed: ${err.message}` });
        },
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);

            // Delete the previous APK from Storage (optional — keeps quota low)
            if (latest?.storagePath && latest.storagePath !== path) {
              try {
                await deleteObject(ref(storage, latest.storagePath));
              } catch (cleanupErr) {
                console.warn('Could not delete old APK:', cleanupErr);
              }
            }

            await setDoc(
              doc(db, 'appReleases', 'android'),
              {
                url,
                storagePath: path,
                version: versionName.trim() || file.name,
                fileName: file.name,
                sizeBytes: file.size,
                uploadedBy: user.uid,
                uploadedByName: user.displayName || user.email || '',
                uploadedAt: serverTimestamp(),
              },
              { merge: true },
            );

            setUploading(false);
            setProgress(100);
            setStatus({ type: 'success', msg: 'Upload complete — footer link updated.' });
            setFile(null);
          } catch (err) {
            setUploading(false);
            setStatus({ type: 'error', msg: `Failed to publish: ${err.message}` });
          }
        },
      );
    } catch (err) {
      setUploading(false);
      setStatus({ type: 'error', msg: err.message });
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Android App
        </h1>
        <Link to="/admin" className="text-sm text-orange hover:underline">
          ← Back to Admin
        </Link>
      </div>

      {/* Current release card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-4 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Current release
        </h2>
        {latest?.url ? (
          <div className="space-y-1 text-sm">
            <p className="text-gray-900 dark:text-white">
              <i className="fab fa-android text-green-500 mr-1" aria-hidden="true" />
              {latest.version || 'Unnamed build'}
            </p>
            <p className="text-xs text-gray-500">
              {latest.sizeBytes ? `${formatBytes(latest.sizeBytes)} · ` : ''}
              {latest.uploadedAt?.toDate?.().toLocaleString?.() || ''}
              {latest.uploadedByName ? ` · by ${latest.uploadedByName}` : ''}
            </p>
            <a
              href={latest.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-xs text-orange hover:underline break-all"
            >
              {latest.url}
            </a>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No APK uploaded yet.</p>
        )}
      </div>

      {/* Upload form */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow p-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Upload new build
        </h2>

        <label className="block mb-3">
          <span className="text-xs text-gray-600 dark:text-gray-400">APK file</span>
          <input
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            onChange={handleFileChange}
            disabled={uploading}
            className="block w-full mt-1 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orange file:text-white hover:file:bg-orange-dark file:cursor-pointer disabled:opacity-50"
          />
          {file && (
            <p className="text-xs text-gray-500 mt-1">
              {file.name} · {formatBytes(file.size)}
            </p>
          )}
        </label>

        <label className="block mb-3">
          <span className="text-xs text-gray-600 dark:text-gray-400">Version name</span>
          <input
            type="text"
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            placeholder="e.g. 2.3.1"
            disabled={uploading}
            className="block w-full mt-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange"
          />
        </label>

        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full bg-orange hover:bg-orange-dark text-white py-2.5 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {uploading ? `Uploading… ${progress}%` : 'Upload & Publish'}
        </button>

        {uploading && (
          <div className="mt-3 w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-orange transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {status && (
          <div
            className={`mt-3 text-sm px-3 py-2 rounded-lg ${
              status.type === 'error'
                ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            }`}
          >
            {status.msg}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-4">
        Note: apps downloaded from outside the Play Store need "Install unknown apps"
        permission enabled. Users will see a warning — that's normal for sideloaded APKs.
      </p>
    </div>
  );
};

export default AdminAndroidApp;